/**
 * PaymentPage — handles Stripe Checkout redirects and payment management.
 *
 * Routes:
 *   /payment/success?session_id=xxx   — after successful checkout
 *   /payment/cancelled                — after user cancels checkout
 *   /payment/team/:teamId             — coach dashboard of all team payments
 *   /payment/pay/:teamId              — player-facing "pay now" page
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, CreditCard, RefreshCw, ChevronLeft, Users, DollarSign } from 'lucide-react';
import api from '../services/api.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import { useAuth } from '../context/AuthContext.jsx';
import toast from 'react-hot-toast';

// ── Shared helpers ────────────────────────────────────────────────────────────
const fmt = (cents, currency = 'cad') =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency.toUpperCase() })
    .format(cents / 100);

const STATUS_BADGE = {
  paid:     { label: '✅ Paid',    bg: 'var(--goal-a12)',    color: 'var(--goal)' },
  pending:  { label: '⏳ Pending', bg: 'rgba(255,149,0,0.1)', color: 'var(--puck)' },
  failed:   { label: '❌ Failed',  bg: 'var(--penalty-a10)', color: 'var(--penalty)' },
  refunded: { label: '↩ Refunded', bg: 'var(--surface-3)',   color: 'var(--text-2)' },
};

// ── Success Page ───────────────────────────────────────────────────────────────
function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const teamId    = searchParams.get('team_id');
  const gameId    = searchParams.get('game_id');
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(!!sessionId);
  const [polledOut, setPolledOut] = useState(false);

  // FIX #33: poll up to 6 times (every ~2s), stopping early once the webhook
  // confirms the payment as 'paid' or 'failed'. The previous version
  // unconditionally rendered "Payment Received!" on mount, which lied to the
  // user when the webhook hadn't fired yet (or failed entirely).
  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setPayment(data);
        attempts += 1;
        if (data.status === 'paid' || data.status === 'failed') {
          setLoading(false);
          // Apr 2026 — surface the Activity nav item immediately when
          // a FIRST payment clears. updateUser merges keys idempotently,
          // so a repeat call with the same value is a no-op — that's
          // why we don't need `user` in the dep array (avoiding a
          // re-run loop after the state merge).
          if (data.status === 'paid' && updateUser) {
            updateUser({ hasPaymentActivity: true });
          }
          return;
        }
        if (attempts < 6) {
          setTimeout(poll, 2000);
        } else {
          setPolledOut(true);
          setLoading(false);
        }
      } catch {
        if (cancelled) return;
        setLoading(false);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, updateUser]);

  const isPaid    = payment?.status === 'paid';
  const isFailed  = payment?.status === 'failed';
  const isPending = !isPaid && !isFailed;

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
      <div className="glass max-w-sm w-full p-8 text-center animate-slide-up">
        <div style={{ fontSize: 56, marginBottom: 16 }}>🏒</div>

        {isPaid && (
          <>
            <CheckCircle size={48} style={{ color: 'var(--goal)', margin: '0 auto 16px' }} />
            <h1 className="section-title text-2xl mb-2">Payment Received!</h1>
            <p className="text-secondary text-sm mb-6">
              {fmt(payment.amount_cents, payment.currency)} — {payment.description || payment.payment_type?.replace(/_/g, ' ')}
            </p>
          </>
        )}

        {isFailed && (
          <>
            <XCircle size={48} style={{ color: 'var(--penalty)', margin: '0 auto 16px' }} />
            <h1 className="section-title text-2xl mb-2">Payment Failed</h1>
            <p className="text-secondary text-sm mb-6">
              The payment didn't go through. You can try again from the team page.
            </p>
          </>
        )}

        {isPending && loading && (
          <>
            <div className="spinner mx-auto my-4" />
            <h1 className="section-title text-2xl mb-2">Processing payment…</h1>
            <p className="text-secondary text-sm mb-6">
              Hang tight while we confirm with the bank. This usually takes a few seconds.
            </p>
          </>
        )}

        {isPending && !loading && polledOut && (
          <>
            <RefreshCw size={48} style={{ color: 'var(--puck)', margin: '0 auto 16px' }} />
            <h1 className="section-title text-2xl mb-2">Still processing</h1>
            <p className="text-secondary text-sm mb-6">
              Your payment is taking longer than usual to confirm. We'll send a notification when it's complete.
            </p>
          </>
        )}

        <button onClick={() => navigate(gameId ? `/game/${gameId}` : teamId ? `/team/${teamId}` : '/dashboard')}
          className="btn-ice w-full justify-center">
          {gameId ? 'Back to Game' : 'Back to Team'}
        </button>
      </div>
    </div>
  );
}

// ── Cancelled Page ─────────────────────────────────────────────────────────────
function CancelledPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('team_id');
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
      <div className="glass max-w-sm w-full p-8 text-center animate-slide-up">
        <XCircle size={48} style={{ color: 'var(--text-2)', margin: '0 auto 20px' }} />
        <h1 className="section-title text-2xl mb-2">Payment Cancelled</h1>
        <p className="text-secondary text-sm mb-6">No charge was made. You can pay later from the team page.</p>
        <button onClick={() => navigate(teamId ? `/team/${teamId}` : '/dashboard')}
          className="btn-ghost w-full justify-center">
          Back to Team
        </button>
      </div>
    </div>
  );
}

// ── Coach Payment Dashboard ────────────────────────────────────────────────────
function TeamPaymentDashboard() {
  const { teamId } = useParams();
  const { isCoach } = useTeamRole(teamId);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [filter, setFilter] = useState('all'); // all | paid | pending

  useEffect(() => { loadPayments(); }, [teamId]);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payments/team/${teamId}`);
      setPayments(data);
    } catch (err) {
      toast.error('Failed to load payments');
    }
    setLoading(false);
  };

  const sendRequest = async (paymentType, userIds) => {
    setRequesting(true);
    try {
      const { data } = await api.post('/payments/request', { teamId, paymentType, userIds });
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send request');
    }
    setRequesting(false);
  };

  if (!isCoach) {
    return <div className="p-8 text-center text-secondary">Coaches only.</div>;
  }

  const filtered = filter === 'all' ? payments : payments.filter(p => p.status === filter);
  const totalPaid = payments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amountCents, 0);
  const totalPending = payments.filter(p => p.status === 'pending').reduce((s, p) => s + p.amountCents, 0);
  const currency = payments[0]?.currency || 'cad';

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/team/${teamId}`} className="btn-ghost px-3 py-2">
          <ChevronLeft size={16} />
        </Link>
        <h1 className="section-title text-2xl">💳 Payment Dashboard</h1>
        <button onClick={loadPayments} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)' }}>
          <RefreshCw size={15} />
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card text-center">
          <p className="text-xs text-secondary label mb-1">Total Collected</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--goal)' }}>{fmt(totalPaid, currency)}</p>
          <p className="text-xs text-secondary">{payments.filter(p => p.status === 'paid').length} payments</p>
        </div>
        <div className="card text-center">
          <p className="text-xs text-secondary label mb-1">Pending</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--puck)' }}>{fmt(totalPending, currency)}</p>
          <p className="text-xs text-secondary">{payments.filter(p => p.status === 'pending').length} outstanding</p>
        </div>
      </div>

      {/* Quick request buttons */}
      <div className="card mb-6">
        <h2 className="font-bold mb-3 flex items-center gap-2">
          <Users size={15} style={{ color: 'var(--ice)' }} /> Send Payment Request
        </h2>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => sendRequest('game_fee', [])} disabled={requesting}
            className="btn-ghost text-sm px-4 py-2">
            {requesting ? <div className="spinner" style={{ width: 14, height: 14 }} /> : '📨 Game Fee → All Members'}
          </button>
          <button onClick={() => sendRequest('season_fee', [])} disabled={requesting}
            className="btn-ghost text-sm px-4 py-2">
            {requesting ? <div className="spinner" style={{ width: 14, height: 14 }} /> : '📨 Season Fee → All Members'}
          </button>
          <button onClick={() => sendRequest('spare_fee', [])} disabled={requesting}
            className="btn-ghost text-sm px-4 py-2">
            {requesting ? <div className="spinner" style={{ width: 14, height: 14 }} /> : '📨 Spare Fee → All Spares'}
          </button>
        </div>
      </div>

      {/* Filter tabs — flex-wrap so 4 tabs don't force horizontal
          overflow at ~390px (matches the flex-wrap already used on
          the "Quick request buttons" row above). */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {['all','paid','pending','failed'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className="text-xs font-bold px-3 py-1.5 rounded-lg capitalize transition-all"
            style={{
              background: filter === f ? 'var(--ice-a15)' : 'var(--surface-2)',
              color: filter === f ? 'var(--ice)' : 'var(--text-2)',
              border: `1px solid ${filter === f ? 'var(--ice-a30)' : 'var(--border)'}`,
            }}>
            {f}
          </button>
        ))}
      </div>

      {/* Payments list */}
      {loading ? (
        <div className="card p-8 text-center"><div className="spinner mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="card p-8 text-center text-secondary">No payments yet.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(pay => {
            const badge = STATUS_BADGE[pay.status] || STATUS_BADGE.pending;
            return (
              <div key={pay.id} className="card flex items-center gap-4 py-3 px-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{pay.playerName}</p>
                  <p className="text-xs text-secondary truncate">{pay.description || pay.paymentType?.replace('_', ' ')}</p>
                  <p className="text-xs text-secondary">{new Date(pay.createdAt).toLocaleDateString('en-CA')}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-sm">{fmt(pay.amountCents, pay.currency)}</p>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-md"
                    style={{ background: badge.bg, color: badge.color }}>
                    {badge.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Player "Pay Now" Page ──────────────────────────────────────────────────────
function PlayerPayPage() {
  const { teamId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate     = useNavigate();
  const { updateUser } = useAuth();
  const paymentType  = searchParams.get('type') || 'game_fee';
  const playerGameId = searchParams.get('game_id') || null;
  const payResult    = searchParams.get('payment'); // 'success' | 'cancelled' from Stripe redirect
  const [team,    setTeam]    = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying,  setPaying]  = useState(false);

  // Apr 2026 — when the user comes back with ?payment=success, the
  // webhook has (or will shortly have) flipped their first payment to
  // 'paid'. Nudge the cached /auth/me user so the Activity nav item
  // surfaces without requiring a reload. Idempotent — safe to call
  // even when hasPaymentActivity is already true.
  useEffect(() => {
    if (payResult === 'success' && updateUser) {
      updateUser({ hasPaymentActivity: true });
    }
  }, [payResult, updateUser]);

  useEffect(() => {
    Promise.all([
      api.get(`/teams/${teamId}`),
      api.get(`/payments/my/${teamId}`),
    ]).then(([teamRes, payRes]) => {
      setTeam(teamRes.data);
      setPayments(payRes.data);
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [teamId]);

  const startPayment = async (type) => {
    setPaying(true);
    try {
      const { data } = await api.post('/payments/checkout', {
        teamId, gameId: playerGameId, paymentType: type,
        returnPath: `/payment/pay/${teamId}`,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Payment setup failed');
      setPaying(false);
    }
  };

  // Show full-screen success/cancelled page when returning from Stripe
  if (payResult === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
        <div className="glass max-w-sm w-full p-8 text-center animate-slide-up">
          <CheckCircle size={52} style={{ color: 'var(--goal)', margin: '0 auto 16px' }} />
          <h1 className="section-title text-2xl mb-2">Payment Confirmed!</h1>
          <p className="text-secondary text-sm mb-6">You're all set. Your team fee has been received.</p>
          <button onClick={() => navigate(`/team/${teamId}`)} className="btn-ice w-full justify-center">
            Back to Team
          </button>
        </div>
      </div>
    );
  }
  if (payResult === 'cancelled') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
        <div className="glass max-w-sm w-full p-8 text-center animate-slide-up">
          <XCircle size={52} style={{ color: 'var(--text-2)', margin: '0 auto 20px' }} />
          <h1 className="section-title text-2xl mb-2">Payment Cancelled</h1>
          <p className="text-secondary text-sm mb-6">No charge was made. You can pay any time from the team page.</p>
          <button onClick={() => navigate(`/team/${teamId}`)} className="btn-ghost w-full justify-center">
            Back to Team
          </button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;

  const settings = team?.settings || {};
  const currency = settings.payment_currency || 'cad';

  if (!settings.payment_enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
        <div className="glass max-w-sm w-full p-8 text-center">
          <CreditCard size={40} style={{ color: 'var(--text-2)', margin: '0 auto 16px' }} />
          <h1 className="section-title text-xl mb-2">Payments not enabled</h1>
          <p className="text-secondary text-sm">This team hasn't set up payment collection yet.</p>
          <Link to={`/team/${teamId}`} className="btn-ghost mt-4 mx-auto">Back to Team</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/team/${teamId}`} className="btn-ghost px-3 py-2"><ChevronLeft size={16} /></Link>
        <h1 className="section-title text-2xl">💳 Pay Team Fee</h1>
      </div>

      {/* Payment options */}
      <div className="space-y-3 mb-6">
        {settings.spare_fee_cents && (
          <div className="card flex items-center gap-4">
            <DollarSign size={20} style={{ color: 'var(--ice)', flexShrink: 0 }} />
            <div className="flex-1">
              <p className="font-bold">Spare Fee</p>
              <p className="text-secondary text-sm">Per-game fee for spares</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg">{fmt(settings.spare_fee_cents, currency)}</p>
              <button onClick={() => startPayment('spare_fee')} disabled={paying}
                className="btn-ice text-xs px-3 py-1.5 mt-1">
                {paying ? <div className="spinner" style={{ width: 12, height: 12 }} /> : 'Pay Now'}
              </button>
            </div>
          </div>
        )}
        {/* Apr 2026 — uses season_fee_cents (the dedicated field set
            via the Season Fees toggle). Previously read member_fee_cents
            (legacy per-game member fee), which is being retired. */}
        {settings.season_fee_cents && (
          <div className="card flex items-center gap-4">
            <DollarSign size={20} style={{ color: 'var(--goal)', flexShrink: 0 }} />
            <div className="flex-1">
              <p className="font-bold">Season Fee</p>
              <p className="text-secondary text-sm">{settings.payment_description || `${team.name} membership`}</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg">{fmt(settings.season_fee_cents, currency)}</p>
              <button onClick={() => startPayment('season_fee')} disabled={paying}
                className="btn-ice text-xs px-3 py-1.5 mt-1">
                {paying ? <div className="spinner" style={{ width: 12, height: 12 }} /> : 'Pay Now'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* My payment history */}
      {payments.length > 0 && (
        <div>
          <h2 className="font-bold mb-3 text-sm" style={{ color: 'var(--text-2)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>My Payment History</h2>
          <div className="space-y-2">
            {payments.map(pay => {
              const badge = STATUS_BADGE[pay.status] || STATUS_BADGE.pending;
              return (
                <div key={pay.id} className="card flex items-center gap-3 py-2.5 px-3">
                  <div className="flex-1 text-sm">
                    <span className="font-medium">{pay.description || pay.paymentType?.replace('_', ' ')}</span>
                    <span className="text-secondary ml-2">{new Date(pay.createdAt).toLocaleDateString('en-CA')}</span>
                  </div>
                  <span className="font-bold text-sm">{fmt(pay.amountCents, pay.currency)}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-md"
                    style={{ background: badge.bg, color: badge.color }}>
                    {badge.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Router ─────────────────────────────────────────────────────────────────────
export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const { page, teamId } = useParams();

  if (page === 'success')   return <SuccessPage />;
  if (page === 'cancelled') return <CancelledPage />;
  if (page === 'team')      return <TeamPaymentDashboard />;
  if (page === 'pay')       return <PlayerPayPage />;

  return <div className="p-8 text-center text-secondary">Page not found.</div>;
}
